import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from 'src/app/app.service';
import { Product } from 'src/app/models/interface/product/product';
import { AddIpRestDto, DeleteIpRestDto, IpDto, IpService, ModifyIpRestDto, ServiceIpInfoResponse } from 'src/app/services/api/ip/ip.service';
import { NotificationLevel, NotificationsService } from 'src/app/services/api/notifications/notifications.service';
import { ProductService } from 'src/app/services/api/product/product.service';
import { ProductDepthLevelRfsActionsComponent } from '../product-depth-level-rfs-actions.component';

@Component({
  selector: 'app-ip-list',
  templateUrl: './ip-list.component.html',
  styleUrls: ['./ip-list.component.scss']
})
export class IpListComponent implements OnInit {

  ipInfo: ServiceIpInfoResponse;
  remoteId: string;
  item: Product;
  loading: boolean;
  adding: boolean;
  addIpRestDto: AddIpRestDto;
  modifyIpRestDto: ModifyIpRestDto;
  addForm: FormGroup;
  editForm: FormGroup;
  vlans: any[];
  chosenVlan: string;
  displayingOptions: boolean;

  cgnat: boolean;
  vlan631: boolean;

  pfiRequired: boolean;
  acsRequired: boolean;
  msmRequired: boolean;

  displayedColumns: string[] = ['data', 'actions'];

  appData: any;

  cgnatBoxEnabled: boolean;

  serviceId: string;

  constructor(public dialogRef: MatDialogRef<ProductDepthLevelRfsActionsComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private ipService: IpService,
    private notificationsService: NotificationsService,
    private translateService: TranslateService,
    private productService: ProductService,
    private appService: AppService) {
    this.loading = false;
  }

  ngOnInit() {
    this.appData = this.appService.settings;
    this.ipInfo = this.data.ipInfo;
    this.item = this.data.item;
    this.remoteId = this.item.cfses[0].cfsCharValues.find(charValue => charValue.specId == "sc_network_remote_id").value;
    this.serviceId = this.item.cfses[0].serviceId;

    this.checkCgnatBoxEnabled();
    this.updateCgnatAndVlan631Boxes();

    this.vlans = [
      { name: 'No value', value: null },
      { name: '603', value: '603' },
      { name: '605', value: '605' }
    ]

    this.displayingOptions = false;

    this.chosenVlan = this.vlans[0].value;

    this.addForm = new FormGroup({
      serviceId: new FormControl({ value: this.serviceId, disabled: true }),
      ipAddress: new FormControl(''),
      vlan: new FormControl(),
      mac: new FormControl('')
    });

    this.editForm = new FormGroup({
      mac: new FormControl(''),
    });

  }

  checkCgnatBoxEnabled() {
    this.cgnatBoxEnabled = !this.ipInfo.ipList.some(ip => ip.vlan == "603");
  }

  reload(reloadItem: boolean) {
    this.loading = true;
    this.ipService.getIpList(this.remoteId).subscribe(
      (data: ServiceIpInfoResponse) => {
        this.loading = false;
        this.ipInfo = data;
        this.checkCgnatBoxEnabled();
        if (reloadItem) {
          this.reloadItem();
        }
      },
      (error) => {
        this.loading = false;
        this.notificationsService.showNotification(this.translateService.instant('IP_LIST.list_error'), NotificationLevel.ERROR);
      }
    );
  }

  reloadItem() {
    this.loading = true;
    this.productService.getProduct(this.item.id.toString()).subscribe(
      (data: Product) => {
        this.loading = false;
        this.item = data;
        this.updateCgnatAndVlan631Boxes();
      },
      (error) => {
        this.loading = false;
        this.notificationsService.showNotification(this.translateService.instant('IP_LIST.product_error'), NotificationLevel.ERROR);
      }
    );
  }

  add() {
    this.adding = !this.adding;
    this.displayingOptions = false;
  }

  confirmAdd() {
    if (!this.addForm.valid || (!this.ipAddressControl.value && !this.chosenVlan)) {
      this.notificationsService.showNotification(this.translateService.instant('IP_LIST.form_error'), NotificationLevel.ERROR);
      return;
    }
    this.addIpRestDto = {
      serviceId: this.serviceIdControl.value,
      ipAddress: this.ipAddressControl.value ? this.ipAddressControl.value : null,
      vlan: this.chosenVlan,
      mac: this.macControl.value
    };
    this.loading = true;
    this.ipService.addIp(this.addIpRestDto).subscribe(
      (data: any) => {
        if (data && data.resultado !== 'NOK' && data.resultado !== 'KO') {
          this.notificationsService.showNotification(this.translateService.instant('IP_LIST.add_ok') +
            `<a href="${this.appData.baseUrl}${this.appData.basePath}/provision/orders/detail` +
            `?id=${data.descResultado}` +
            `" target="_blank">${data.descResultado}</a>`, NotificationLevel.SUCCESS);
          this.addForm.controls['ipAddress'].reset();
          this.addForm.controls['vlan'].reset();
          this.addForm.controls['mac'].reset();
          this.reload(true);
        }
        else if (data) {
          this.notificationsService.showNotification("ERROR (" + data.codResultado + "): " + data.descResultado, NotificationLevel.ERROR);
          this.loading = false;
        }
        else {
          this.notificationsService.showNotification(this.translateService.instant('IP_LIST.add_error'), NotificationLevel.ERROR);
          this.loading = false;
        }
      },
      (error) => {
        this.notificationsService.showNotification(this.translateService.instant('IP_LIST.add_error'), NotificationLevel.ERROR);
        this.loading = false;
      }
    );
  }

  edit(ipDto: IpDto) {
    ipDto.editing = !ipDto.editing;
  }

  confirmEdit(ipDto: IpDto) {
    if (!this.editForm.valid) {
      this.notificationsService.showNotification(this.translateService.instant('IP_LIST.form_error'), NotificationLevel.ERROR);
      return;
    }
    this.modifyIpRestDto = {
      serviceId: this.serviceId,
      ipId: ipDto.ip_id,
      ipAddress: ipDto.hostaddr,
      mac: this.editMacControl.value
    };
    this.loading = true;
    this.ipService.modifyIp(this.modifyIpRestDto).subscribe(
      (data: any) => {
        if (data && data.resultado !== 'NOK' && data.resultado !== 'KO') {
          this.notificationsService.showNotification(this.translateService.instant('IP_LIST.modify_ok'), NotificationLevel.SUCCESS);
          this.editForm.reset();
          this.reload(true);
        }
        else if (data) {
          this.notificationsService.showNotification("ERROR (" + data.codResultado + "): " + data.descResultado, NotificationLevel.ERROR);
          this.loading = false;
        }
        else {
          this.notificationsService.showNotification(this.translateService.instant('IP_LIST.modify_error'), NotificationLevel.ERROR);
          this.loading = false;
        }
      },
      (error) => {
        this.notificationsService.showNotification(this.translateService.instant('IP_LIST.modify_error'), NotificationLevel.ERROR);
        this.loading = false;
      }
    );
  }

  delete(ipDto: IpDto) {
    ipDto.deleting = true;
  }

  confirmDelete(ipDto: IpDto) {
    const deleteIpRestDto = {
      serviceId: this.serviceId,
      ipId: ipDto.ip_id,
      ipAddress: ipDto.hostaddr
    } as DeleteIpRestDto;
    this.loading = true;
    this.ipService.deleteIp(deleteIpRestDto).subscribe(
      (data: any) => {
        if (data && data.resultado !== 'NOK' && data.resultado !== 'KO') {
          this.notificationsService.showNotification(this.translateService.instant('IP_LIST.delete_ok') +
            `<a href="${this.appData.baseUrl}${this.appData.basePath}/provision/orders/detail` +
            `?id=${data.descResultado}` +
            `" target="_blank">${data.descResultado}</a>`, NotificationLevel.SUCCESS);
          ipDto.deleting = false;
          this.reload(true);
        }
        else if (data) {
          this.notificationsService.showNotification("ERROR (" + data.codResultado + "): " + data.descResultado, NotificationLevel.ERROR);
          ipDto.deleting = false;
          this.loading = false;
        }
        else {
          this.notificationsService.showNotification(this.translateService.instant('IP_LIST.delete_error'), NotificationLevel.ERROR);
          ipDto.deleting = false;
          this.loading = false;
        }
      },
      (error) => {
        this.notificationsService.showNotification(this.translateService.instant('IP_LIST.delete_error'), NotificationLevel.ERROR);
        ipDto.deleting = false;
        this.loading = false;
      }
    );
  }

  discardDelete(ipDto: IpDto) {
    ipDto.deleting = false;
  }

  displayOptions() {
    this.displayingOptions = !this.displayingOptions;
    this.adding = false;
  }

  reprovision() {
    this.loading = true;
    const reprovisionDto = {
      iua: this.serviceId,
      vlan631: this.vlan631,
      cgnat: this.cgnat,
      pfiRequired: this.pfiRequired,
      acsRequired: this.acsRequired,
      msmRequired: this.msmRequired
    };
    this.ipService.reprovisionWithCgnatAndVlan631(reprovisionDto).subscribe(
      (data: any) => {
        if (data && data.resultado !== 'NOK' && data.resultado !== 'KO') {
          this.notificationsService.showNotification(this.translateService.instant('IP_LIST.reprovision_ok') +
            `<a href="${this.appData.baseUrl}${this.appData.basePath}/provision/orders/detail` +
            `?id=${data.descResultado}` +
            `" target="_blank">${data.descResultado}</a>`, NotificationLevel.SUCCESS);
          this.dialogRef.close(true);
        }
        else if (data) {
          this.notificationsService.showNotification("ERROR (" + data.codResultado + "): " + data.descResultado, NotificationLevel.ERROR);
          this.updateCgnatAndVlan631Boxes();
          this.loading = false;
        }
        else {
          this.notificationsService.showNotification(this.translateService.instant('IP_LIST.reprovision_error'), NotificationLevel.ERROR);
          this.updateCgnatAndVlan631Boxes();
          this.loading = false;
        }
        this.reload(true);
      },
      (error) => {
        this.notificationsService.showNotification(this.translateService.instant('IP_LIST.reprovision_error'), NotificationLevel.ERROR);
        this.updateCgnatAndVlan631Boxes();
        this.loading = false;
      }
    );
  }

  updateCgnatAndVlan631Boxes() {
    this.vlan631 = this.item.cfses[0].cfsCharValues.find(cfsCharValue => cfsCharValue.specId == "sc_ftth_vlan631_enabled") ? (this.item.cfses[0].cfsCharValues.find(cfsCharValue => cfsCharValue.specId == "sc_ftth_vlan631_enabled").value == "true" ? true : false) : this.vlan631;
    this.cgnat = this.item.cfses[0].cfsCharValues.find(cfsCharValue => cfsCharValue.specId == "sc_ftth_cgnat_enabled") ? (this.item.cfses[0].cfsCharValues.find(cfsCharValue => cfsCharValue.specId == "sc_ftth_cgnat_enabled").value == "true" ? true : false) : this.cgnat;
  
    this.updateRequiredValues();
  }

  updateRequiredValues(){
    if(this.vlan631){
      this.pfiRequired = true;
      this.acsRequired = true;
    }

    if(this.cgnat){
      this.msmRequired = true;
    }
  }


  get serviceIdControl(): FormControl {
    return this.addForm.get('serviceId') as FormControl;
  }

  get ipAddressControl(): FormControl {
    return this.addForm.get('ipAddress') as FormControl;
  }

  get macControl(): FormControl {
    return this.addForm.get('mac') as FormControl;
  }

  get vlanControl(): FormControl {
    return this.addForm.get('vlan') as FormControl;
  }

  get editMacControl(): FormControl {
    return this.editForm.get('mac') as FormControl;
  }

}
